<template>
  <main class="assignments-list-page">
    <CompanyBreadcrumb :aria-label="$t(pageTitle)"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <template v-if="errorMessages.length">
      <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: red">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </template>
    <div v-if="successMessage" class="uk-alert-success" uk-alert>
      <a class="uk-alert-close" uk-close></a>
      <p style="color: green">
        <i class="uil-exclamation-triangle"></i> {{ successMessage }}
      </p>
    </div>

    <div class="assignments-page-header">
      <div class="assignments-page-header-left">
        <h2 class="assignments-page-title">
          {{ $t(pageTitle) }} ({{ totalItems }})
        </h2>
      </div>
      <div v-if="statusFilter" class="assignments-page-header-right">
        <router-link :to="{ name: 'company.assignments.add' }">
          <button :disabled="!$hasPermission('create_assignment')" :style="{ cursor: !$hasPermission('create_assignment') ? 'not-allowed' : 'pointer' }"
                  class="btn btn-default assignments-add-btn"
                  type="button">
            <i class="uil-location-arrow"></i> {{ $t('menu.company_dashboard.create_assignment') }}
          </button>
        </router-link>
      </div>
    </div>

    <div class="assignments-toolbar">
      <div class="assignments-toolbar-search">
        <search-input v-model="filter.search"
                      :aria-label="$t('general.search')"
                      :control-height="48"
                      :debounce="0"
                      :placeholder="$t('general.search')"
        />
      </div>
      <div class="assignments-toolbar-actions">
        <multiple-select-input v-model="selectedViewMode" :allowEmpty="false" :multiple="false" :required="false"
                               :searchable="false" :showLabels="false" :values="viewModeOptions"
                               class="view-mode-select"
                               @select="changeViewMode"/>
        <multiple-select-input v-model="selectedSort" :allowEmpty="false" :multiple="false" :required="false"
                               :searchable="false" :showLabels="false" :values="sortOptions" class="order-select"
                               @select="sortOrder"/>
        <button :aria-label="$t('general.filter')"
                :class="{ 'assignments-filter-btn--active': filterVisible }"
                class="btn btn-icon btn-hover btn-circle assignments-filter-btn"
                type="button"
                @click="filterVisible = !filterVisible">
          <i class="uil-filter"></i>
        </button>
      </div>
    </div>

    <div class="assignments-layout">
      <div class="assignments-main">
        <!-- GRID VIEW -->
        <div v-if="viewMode === 'grid'" class="assignments-grid">
          <article v-for="assignment in displayedAssignments" :key="assignment.id" class="card assignments-card">
            <div class="position-relative">
              <img :alt="assignment.name" :src="assignment.cover_image_link" class="assignments-card-cover"/>
              <span v-if="!assignment.status_id" class="badge badge-danger position-absolute"
                    style="top: 10px; right: 10px; font-size: 0.75rem; padding: 0.35rem 0.65rem">
                {{ $t("general.passive") }}
              </span>
            </div>
            <div class="assignments-card-body">
              <h6 class="assignments-card-title">{{ assignment.name }}</h6>
              <span v-if="assignment.repeatable" class="assignments-card-meta">{{ assignment.order_number }}</span>
            </div>
            <div class="assignments-card-actions">
              <router-link :to="{ name: 'company.assignments.preview', params: { id: assignment.id } }"
                           class="btn btn-secondary btn-sm assignments-card-btn">
                <i class="uil-eye"></i>
                <span class="assignments-card-btn-label">{{ $t("certification.preview") }}</span>
              </router-link>
              <router-link :to="{ name: 'company.assignments.edit', params: { id: assignment.id } }"
                           class="btn btn-info btn-sm assignments-card-btn">
                <i class="uil-play-circle"></i>
                <span class="assignments-card-btn-label">{{ $t("faq_categories.edit") }}</span>
              </router-link>
              <a :disabled="!$hasPermission('delete_assignment')" :style="{ cursor: !$hasPermission('delete_assignment') ? 'not-allowed' : 'pointer' }"
                 class="btn btn-danger btn-sm assignments-card-btn"
                 href="#"
                 @click.prevent="removeAssignment(assignment.id)">
                <i class="uil-minus-circle"></i>
                <span class="assignments-card-btn-label">{{ $t("general.delete") }}</span>
              </a>
            </div>
            <div class="assignments-card-date">
              <a :uk-tooltip="`title: ${$t('general.start_end_date')}; pos: bottom-center`" class="text-muted">
                <i class="uil-calendar-alt"></i>
                {{ assignment.start_end }}
              </a>
            </div>
          </article>
        </div>

        <!-- LIST VIEW -->
        <div v-else class="card assignments-list-card">
          <div class="assignments-list-summary">
            <div>
              <span class="btn btn-outline-dark btn-sm mr-2">{{ $t("general.assignments_total") }} : {{
                  totalItems
                }}</span>
              <span class="btn btn-outline-dark btn-sm mr-2">{{
                  $t("general.selected_assignments")
                }} : {{ selectedAssignmentIds.length }}</span>
            </div>
            <div>{{ $t(statusFilter ? "general.active_assignments" : "general.passive_assignments") }}</div>
          </div>

          <div class="assignments-bulk-actions">
            <button class="btn btn-sm btn-secondary assignments-bulk-btn" type="button" @click="toggleAllVisibleButton">
              <i class="uil-check-square"></i> {{
                $t(allVisibleSelected ? "general.deselect_all" : "general.select_all")
              }}
            </button>
            <button v-if="isPassiveList" :disabled="!hasSelection || assignment_loading" class="btn btn-sm btn-success assignments-bulk-btn"
                    type="button" @click="bulkUpdateStatus(true)">
              <i class="uil-check"></i> {{ $t("general.activate_selected_users") }}
            </button>
            <button v-if="!isPassiveList" :disabled="!hasSelection || assignment_loading" class="btn btn-sm btn-danger assignments-bulk-btn"
                    type="button" @click="bulkUpdateStatus(false)">
              <i class="uil-minus-circle"></i> {{ $t("general.inactive_selected_users") }}
            </button>
            <button :disabled="!hasSelection || assignment_loading" class="btn btn-sm btn-secondary assignments-bulk-btn"
                    type="button" @click="bulkDeleteAssignments">
              <i class="uil-trash"></i> {{ $t("general.delete_selected_users") }}
            </button>
          </div>

          <div class="table-responsive">
            <table class="table align-items-center assignments-table">
              <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">{{ $t("general.assignment") }}</th>
                <th scope="col">{{ $t("general.status") }}</th>
                <th scope="col">{{ $t("general.date") }}</th>
                <th scope="col"></th>
              </tr>
              </thead>
              <tbody class="list">
              <tr v-for="assignment in displayedAssignments" :key="`list-${assignment.id}`">
                <th>
                  <input :id="'assignment_checkbox_' + assignment.id" :checked="isAssignmentSelected(assignment.id)"
                         :value="assignment.id" class="assignments-checkbox" type="checkbox"
                         @change="toggleAssignmentSelection(assignment.id, $event.target.checked)"/>
                </th>
                <th scope="row">
                  <div class="media align-items-center">
                    <div class="assignments-cover-thumb">
                      <img :src="assignment.cover_image_link" alt=""/>
                    </div>
                    <div class="media-body assignments-table-body">
                      <div class="name h6 mb-0 text-sm">{{ assignment.name }}</div>
                      <small class="d-block font-weight-bold">#{{ assignment.id }}</small>
                      <small class="d-block assignments-table-meta">{{ assignment.description }}</small>
                    </div>
                  </div>
                </th>
                <td :class="assignment.status_id ? 'text-success' : 'text-danger'">
                  {{ assignment.status_id ? $t("general.active") : $t("general.passive") }}
                </td>
                <td>{{ assignment.start_end }}</td>
                <td class="text-right">
                  <div class="assignments-table-actions">
                    <router-link :to="{ name: 'company.assignments.preview', params: { id: assignment.id } }"
                                 :uk-tooltip="$t('certification.preview')"
                                 class="btn btn-icon btn-hover btn-sm btn-circle">
                      <i class="uil-eye"></i>
                    </router-link>
                    <router-link :to="{ name: 'company.assignments.edit', params: { id: assignment.id } }"
                                 :uk-tooltip="$t('faq_categories.edit')"
                                 class="btn btn-icon btn-hover btn-sm btn-circle">
                      <i class="uil-play-circle"></i>
                    </router-link>
                    <a v-if="isPassiveList" :uk-tooltip="$t('general.activate_assignment')" class="btn btn-icon btn-hover btn-sm btn-circle"
                       href="#"
                       @click.prevent="bulkUpdateSingleAssignment(assignment)">
                      <i class="uil-check text-success"></i>
                    </a>
                    <a v-else :uk-tooltip="$t('general.deactivate_assignment')" class="btn btn-icon btn-hover btn-sm btn-circle"
                       href="#"
                       @click.prevent="bulkUpdateSingleAssignment(assignment)">
                      <i class="uil-minus-circle text-danger"></i>
                    </a>
                    <a :disabled="!$hasPermission('delete_assignment')" :style="{ cursor: !$hasPermission('delete_assignment') ? 'not-allowed' : 'pointer' }"
                       :uk-tooltip="$t('general.delete')"
                       class="btn btn-icon btn-hover btn-sm btn-circle"
                       href="#" @click.prevent="removeAssignment(assignment.id)">
                      <i class="uil-trash text-danger"></i>
                    </a>
                  </div>
                </td>
              </tr>
              <tr v-if="!displayedAssignments.length">
                <td class="text-center py-4" colspan="5">{{ $t("general.no_assignments_found") }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="assignments-pagination">
          <pagination v-if="pagination && pagination.hasOwnProperty('links')" :pagination="pagination"
                      @changePage="setPage($event)"></pagination>
        </div>
      </div>

      <aside :class="{ 'sidebar-filter-visible': filterVisible }" class="sidebar-filter card assignments-sidebar">
        <div class="sidebar-filter-contents assignments-filter">
          <h4 class="assignments-filter-title">
            <i class="uil-filter"/> {{ $t("general.filter") }}
          </h4>
          <label class="assignments-filter-option">
            <input :data-id="'all'" checked class="uk-radio" data-type="all" name="filters" type="radio"
                   @change="setType"/>
            <span>{{ $t("general.all") }}</span>
          </label>

          <ul class="sidebar-filter-list uk-accordion" uk-accordion="multiple: true">
            <li class="uk-open">
              <a class="uk-accordion-title assignments-filter-accordion" href="#">{{
                  $t("general.by_category_tag")
                }}</a>
              <div aria-hidden="false" class="uk-accordion-content">
                <div class="assignments-tag-search">
                  <label class="assignments-tag-search-label" for="search-tag">
                    <i class="uil-search"></i>
                    {{ $t("general.tag_search") }}
                  </label>
                  <input id="search-tag" v-model="searchTag" :placeholder="$t('general.type_a_tag')"
                         autocomplete="off" class="form-control assignments-tag-search-input" type="text"/>
                </div>
                <div class="uk-form-controls assignments-tag-list">
                  <label v-for="(item, index) in filteredTags" :key="`filteredTags-${index}`"
                         class="assignments-filter-option">
                    <input :data-id="item.id" class="uk-radio" data-type="hashtag" name="filters" type="radio"
                           @change="setType"/>
                    <span>{{ $t(item.name) }}</span>
                  </label>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </aside>
    </div>
  </main>
</template>

<script>
import Vue from "vue";
import DataTable from "@/assets/components/dataTable/DataTable";
import store from "@/core/services";
import Pagination from "@/assets/components/Pagination";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import SearchInput from "@/view/components/inputs/SearchInput.vue";

import module, {
  BASE_URL,
  MODULE_NAME,
  ERROR,
  ITEMS,
  GET_ITEMS,
  DELETE_ITEM,
  LOADING,
} from "@/core/services/store/assignment.module";
import moment from "moment";
import Swal from "sweetalert2";
import { RESET_POINTS } from "@/core/services/store/company.module";
import axios from "axios";
import AppMultiselect from "@/view/components/select/app-multiselect/app-multiselect.vue";
import _ from "lodash";

export default {
  name: "AssignmentList",
  components: {
    DataTable,
    Pagination,
    DefaultLoading,
    MultipleSelectInput,
    AppMultiselect,
    CompanyBreadcrumb,
    SearchInput
  },
  data() {
    return {
      searchTag: "",
      searchTagList: [],
      items: [],
      totalItems: 0,
      pagination: null,
      successMessage: null,
      errorMessages: [],
      page: 1,
      per_page: 12,
      viewMode: "grid",
      selectedViewMode: null,
      selectedAssignmentIds: [],
      filterVisible: false,
      filter: {
        search: null,
        sort: "desc",
        order_by: "created_at",
        hashtag_id: null
      },
      selectedSort: null,
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t('menu.company_dashboard.assignments') },
        { name: this.$t(this.pageTitle) },
      ];
    },
    filteredTags() {
      const tags = this.searchTagList;
      const search = this.searchTag;
      if (search === "" || tags == null) return tags;

      return tags.filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()));
    },
    displayedAssignments() {
      return Array.isArray(this.items) ? this.items : [];
    },
    sortOptions() {
      return [
        { id: "name_asc", name: this.$t("sorting.a_z"), sort: "asc", order_by: "name" },
        { id: "name_desc", name: this.$t("sorting.z_a"), sort: "desc", order_by: "name" },
        { id: "created_at_asc", name: this.$t("sorting.created_at_asc"), sort: "asc", order_by: "created_at" },
        { id: "created_at_desc", name: this.$t("sorting.created_at_desc"), sort: "desc", order_by: "created_at" },
        { id: "end_date_asc", name: this.$t("sorting.end_date_asc"), sort: "asc", order_by: "end_date" },
        { id: "end_date_desc", name: this.$t("sorting.end_date_desc"), sort: "desc", order_by: "end_date" },
        { id: "start_date_asc", name: this.$t("sorting.start_date_asc"), sort: "asc", order_by: "start_date" },
        { id: "start_date_desc", name: this.$t("sorting.start_date_desc"), sort: "desc", order_by: "start_date" },
      ];
    },
    viewModeOptions() {
      return [
        { id: "grid", name: this.$t("general.card_view") },
        { id: "list", name: this.$t("general.list_view") },
      ];
    },
    assignment_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set() {
      },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set() {
      },
    },
    statusFilter() {
      const query = this.$route.query || {};
      if (this.$route.path.includes("assignments-passive")) return 0;
      const isPassive = "passive" in query || query.status === "passive";
      return isPassive ? 0 : 1;
    },
    pageTitle() {
      return this.$route.path.includes("assignments-passive") ? "general.passive_assignments" : "general.all_assignments";
    },
    isPassiveList() {
      return this.statusFilter === 0;
    },
    hasSelection() {
      return this.selectedAssignmentIds.length > 0;
    },
    allVisibleSelected() {
      return this.displayedAssignments.length > 0 &&
          this.displayedAssignments.every((assignment) => this.selectedAssignmentIds.includes(assignment.id));
    },
  },
  methods: {
    changeViewMode(option) {
      this.viewMode = option.id;
      this.clearSelection();
    },
    clearSelection() {
      this.selectedAssignmentIds = [];
    },
    isAssignmentSelected(id) {
      return this.selectedAssignmentIds.includes(id);
    },
    toggleAssignmentSelection(id, checked) {
      if (checked) {
        if (!this.selectedAssignmentIds.includes(id)) {
          this.selectedAssignmentIds = [...this.selectedAssignmentIds, id];
        }
        return;
      }
      this.selectedAssignmentIds = this.selectedAssignmentIds.filter((itemId) => itemId !== id);
    },
    toggleAllVisibleButton() {
      if (this.allVisibleSelected) {
        this.clearSelection();
        return;
      }
      this.selectedAssignmentIds = this.displayedAssignments.map((assignment) => assignment.id);
    },
    setType(event) {
      const target = event.target;
      const id = target.getAttribute("data-id");
      const type = target.getAttribute("data-type");
      this.filter.file_type_id = this.filter.content_type_id = this.filter.hashtag = null;
      if (type === "hashtag") this.filter.hashtag_id = id;
      else if (type === "file_type_id") this.filter.file_type_id = id;
      else if (type === "content_type_id") this.filter.content_type_id = id;
      else if (type === "all") {
        this.filter.hashtag_id = null;
        this.getAssignmentResult();
      }
      if (type !== "all") this.debouncedFilterType();
    },
    async getFilteredTags() {
      const { data } = await axios.get("/api/assignment/filters?by_user=false");
      this.searchTagList = data;
    },
    formattedItem() {
      const results = [];
      if (this.items === null || !("data" in this.items)) return [];
      this.pagination = this.items;
      this.totalItems = this.items?.total || 0;
      this.items.data.forEach((currentItem) => {
        results.push({
          id: currentItem.id,
          name: currentItem.name,
          hasAssigned: currentItem.users_count,
          cover_image_link: currentItem.cover_image_link,
          description: currentItem.description || "-",
          order_number: currentItem.repeatable_main_item ? this.$t("general.main_content_assignment") : `${currentItem.order_number}. ${this.$t("general.repeated_assignment")}`,
          repeatable: !!(currentItem.repeatable_main_item || currentItem.repeatable),
          start_end: currentItem.start_date && currentItem.end_date
              ? moment(currentItem.start_date).format("DD-MM-YYYY") + " / " + moment(currentItem.end_date).format("DD-MM-YYYY")
              : "-",
          status_id: currentItem.status_id == true || currentItem.status_id === 1 || currentItem.status_id === "1",
        });
      });
      this.items = results;
      return results;
    },
    getAssignmentResult() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          search: this.filter.search,
          page: this.page,
          per_page: this.per_page,
          sort: this.filter.sort,
          order_by: this.filter.order_by,
          with_users: true,
          status_id: this.statusFilter,
          hashtag_id: this.filter.hashtag_id,
        },
      }).then((result) => {
        this.items = result;
        this.formattedItem();
        this.clearSelection();
      });
    },
    async bulkUpdateStatus(status) {
      if (!this.hasSelection) return;

      const ids = this.selectedAssignmentIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));

      try {
        await axios.put("/api/assignments/bulk-update", { ids, status });
        this.successMessage = this.$t(status ? "general.selected_assignments_activated" : "general.selected_assignments_deactivated");
        this.getAssignmentResult();
      } catch (error) {
        this.errorMessages.push(error?.response?.data?.message || error?.message);
      }
    },
    bulkUpdateSingleAssignment(assignment) {
      this.selectedAssignmentIds = [assignment.id];
      this.bulkUpdateStatus(!assignment.status_id);
    },
    async bulkDeleteAssignments() {
      if (!this.hasSelection) return;

      const ids = this.selectedAssignmentIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
      const result = await Swal.fire({
        title: this.$t("general.sure_to_delete", { count: ids.length }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("general.yes"),
        cancelButtonText: this.$t("general.cancel"),
        confirmButtonColor: "#e31748",
      });

      if (!result.isConfirmed) return;

      try {
        await axios.delete("/api/assignments/bulk-destroy", { data: { ids } });
        this.successMessage = this.$t("general.successfully_deleted");
        this.getAssignmentResult();
      } catch (error) {
        this.errorMessages.push(error?.response?.data?.message || error?.message);
      }
    },
    removeAssignment(id) {
      Swal.fire({
        title: this.$t("general.assignment_delete_with_users_confirm"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("general.yes"),
        cancelButtonText: this.$t("general.cancel"),
        confirmButtonColor: "#e31748",
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.$store.dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
          url: BASE_URL + "/" + id,
        }).then(() => {
          if (!this.error) {
            this.successMessage = this.$t("general.successfully_deleted");
            this.getAssignmentResult();
          } else {
            this.errorMessages.push(this.error);
          }
        });
      });
    },
    sortOrder(value) {
      this.page = 1;
      this.filter.sort = value.sort;
      this.filter.order_by = value.order_by;
      this.getAssignmentResult();
    },
    setPage(page) {
      this.page = page;
      this.getAssignmentResult();
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
  },
  mounted() {
    this.selectedSort = this.sortOptions.find((o) => o.id === "created_at_desc");
    this.selectedViewMode = this.viewModeOptions.find((option) => option.id === this.viewMode);
    this.getAssignmentResult();
    this.getFilteredTags();
  },
  watch: {
    "$i18n.locale"() {
      this.selectedSort = this.sortOptions.find((option) => option.id === this.selectedSort?.id) || this.sortOptions[0];
      this.selectedViewMode = this.viewModeOptions.find((option) => option.id === this.viewMode) || this.viewModeOptions[0];
    },
    $route() {
      this.page = 1;
      this.getAssignmentResult();
    },
    "filter.search": function () {
      this.debouncedFilterSearch();
    },
    successMessage: function () {
      this.debouncedResetValues();
    },
    errorMessages: function () {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getAssignmentResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
    this.debouncedFilterType = _.debounce(this.getAssignmentResult, 300);
  },
};
</script>

<style scoped>
/* ═══════ Base / Variables ═══════ */
.assignments-list-page {
  --assign-primary: var(--primaryColor, #3e416d);
  --assign-text: #1e293b;
  --assign-muted: #64748b;
  --assign-border: #e2e8f0;
  --assign-soft: #f6f7fb;
  min-width: 0;
}

/* ═══════ Page Header ═══════ */
.assignments-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.assignments-page-title {
  margin: 0;
  color: var(--assign-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.assignments-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.assignments-add-btn:disabled {
  opacity: 0.5;
}

.assignments-add-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

/* ═══════ Toolbar ═══════ */
.assignments-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.assignments-toolbar-search {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 480px;
}

.assignments-toolbar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.assignments-toolbar-actions ::v-deep .form-group {
  margin-bottom: 0;
}

.assignments-filter-btn {
  display: none;
}

.order-select,
.view-mode-select {
  min-width: 200px;
}

.assignments-toolbar-actions ::v-deep .multiselect__single {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assignments-toolbar-actions ::v-deep .multiselect__content-wrapper {
  max-height: none !important;
}

/* ═══════ Layout (main + sidebar) ═══════ */
.assignments-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 16px;
  align-items: start;
}

.assignments-main,
.assignments-sidebar {
  min-width: 0;
}

/* ═══════ Sidebar ═══════ */
.assignments-sidebar {
  position: sticky;
  top: 80px;
  padding: 16px;
  border: 1px solid var(--assign-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.assignments-filter-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  color: var(--assign-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  text-transform: none;
}

.assignments-filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  margin: 0;
  padding: 4px 0;
  color: var(--assign-text);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  cursor: pointer;
}

.assignments-filter-accordion {
  color: var(--assign-text);
  font-size: 14px;
  font-weight: 600;
}

.assignments-tag-search {
  margin-bottom: 8px;
}

.assignments-tag-search-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: var(--assign-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.assignments-tag-search-input {
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  font-size: 15px;
  line-height: 1.5;
}

.assignments-tag-list {
  max-height: 160px;
  overflow-y: auto;
}

/* ═══════ Grid View ═══════ */
.assignments-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.assignments-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  height: 100%;
  margin: 0;
  border: 1px solid var(--assign-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.assignments-card-cover {
  display: block;
  width: 100%;
  height: 180px;
  object-fit: cover;
}

.assignments-card-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  padding: 16px 16px 8px;
  text-align: center;
}

.assignments-card-title {
  margin: 0;
  color: var(--assign-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.assignments-card-meta {
  color: var(--assign-muted);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
}

.assignments-card-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px 16px;
}

.assignments-card-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  margin: 0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.assignments-card-btn i {
  line-height: 1;
}

.assignments-card-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.assignments-card-date {
  padding: 0 16px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--assign-muted);
}

/* ═══════ List View ═══════ */
.assignments-list-card {
  border: 1px solid var(--assign-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.assignments-list-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
  font-size: 14px;
}

.assignments-bulk-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 16px;
}

.assignments-bulk-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.assignments-bulk-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.assignments-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.assignments-table {
  min-width: 600px;
  font-size: 14px;
}

.assignments-table th,
.assignments-table td {
  padding: 12px 8px;
  vertical-align: middle;
}

.assignments-cover-thumb {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--assign-soft);
  flex-shrink: 0;
}

.assignments-cover-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.assignments-table-body {
  margin-left: 16px;
}

.assignments-table-meta {
  color: var(--assign-muted);
  font-size: 12px;
  max-width: 320px;
  white-space: normal;
}

.assignments-table-actions {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
}

.assignments-table-actions .btn {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.assignments-pagination {
  margin-top: 16px;
}

/* ═══════ Desktop: search input height ═══════ */
@media (min-width: 960px) {
  .assignments-toolbar-search ::v-deep .uk-search-input {
    box-sizing: border-box;
    min-height: 38px;
    /* Sol padding IKON GENISLIGI kadar olmali (40px). 15px'ti ve
       .uk-search-icon-flip soldaki 40px'i kapladigi icin metin ikonun
       altindan geciyordu. Buradaki amac yalnizca yukseklik/font
       ayarlamakti, padding shorthand'i yanlislikla sol insetti bozmus. */
    padding: 8px 40px 8px 40px;
    font-size: 15px;
    line-height: 1.4;
  }
}

/* ═══════ Tablet: 960–1199 ═══════ */
@media (max-width: 1199px) and (min-width: 960px) {
  .assignments-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .order-select,
  .view-mode-select {
    min-width: 160px;
  }
}

/* ═══════ Mobile: <960px ═══════ */
@media (max-width: 959px) {
  .assignments-list-page {
    padding: 0 0 24px;
  }

  /* ── Header ── */
  .assignments-page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .assignments-page-title {
    font-size: 24px;
    font-weight: 700;
  }

  .assignments-add-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  /* ── Toolbar ── */
  .assignments-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .assignments-toolbar-search {
    width: 100%;
    max-width: none;
  }

  .assignments-toolbar-actions {
    justify-content: flex-end;
    width: 100%;
  }

  .order-select,
  .view-mode-select {
    min-width: 0;
    flex: 1;
  }

  .assignments-toolbar-actions ::v-deep .btn-circle,
  .assignments-filter-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }

  .assignments-filter-btn--active {
    color: #2f6fe4;
    background: #edf3ff;
  }

  /* ── Layout ── */
  .assignments-layout {
    grid-template-columns: 1fr;
  }

  .assignments-sidebar {
    position: static;
    top: auto;
    order: -1;
    display: none;
  }

  .assignments-sidebar.sidebar-filter-visible {
    display: block;
  }

  .assignments-list-page .assignments-sidebar .sidebar-filter-contents {
    display: block !important;
    padding: 0;
  }

  .assignments-filter-option {
    min-height: 48px;
    font-size: 16px;
  }

  .assignments-filter-accordion {
    display: flex;
    align-items: center;
    min-height: 48px;
    font-size: 16px;
  }

  .assignments-tag-search-input {
    height: 48px;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  /* ── Grid View ── */
  .assignments-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .assignments-card {
    border-radius: 14px;
  }

  .assignments-card-cover {
    height: auto;
    aspect-ratio: 521 / 288;
  }

  .assignments-card-body {
    gap: 4px;
    padding: 8px 8px 0;
  }

  .assignments-card-title {
    display: -webkit-box;
    overflow: hidden;
    font-size: 16px;
    line-height: 1.4;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .assignments-card-meta {
    overflow: hidden;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .assignments-card-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 12px 12px;
  }

  .assignments-card-btn {
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 44px;
    padding: 8px 16px;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    border-radius: 10px;
  }

  .assignments-card-btn i {
    margin: 0;
    font-size: 16px;
    line-height: 1;
  }

  .assignments-card-btn-label {
    display: inline;
  }

  .assignments-card-date {
    padding: 4px 8px 12px;
    font-size: 12px;
  }

  /* ── List View ── */
  .assignments-list-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
  }

  .assignments-list-summary .btn {
    font-size: 13px;
    padding: 4px 10px;
  }

  .assignments-bulk-actions {
    flex-direction: column;
    padding: 0 12px 12px;
  }

  .assignments-bulk-btn {
    width: 100%;
    min-height: 44px;
    justify-content: center;
    font-size: 14px;
  }

  .assignments-table {
    min-width: 480px;
    font-size: 13px;
  }

  .assignments-table th,
  .assignments-table td {
    padding: 10px 6px;
  }

  .assignments-cover-thumb {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }

  .assignments-table-body {
    margin-left: 8px;
  }

  .assignments-table-body .name {
    font-size: 13px;
  }

  .assignments-table-meta {
    max-width: 180px;
    font-size: 11px;
  }

  .assignments-table-actions .btn {
    min-width: 40px;
    min-height: 40px;
  }

  .assignments-checkbox {
    width: 20px;
    height: 20px;
  }
}

/* ═══════ Small mobile: <480px ═══════ */
@media (max-width: 479px) {
  .assignments-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .assignments-card-cover {
    aspect-ratio: 16 / 9;
  }

  .assignments-card-body {
    gap: 6px;
    padding: 12px 12px 4px;
  }

  .assignments-card-title {
    font-size: 15px;
    -webkit-line-clamp: 2;
  }

  .assignments-card-actions {
    flex-direction: column;
    gap: 8px;
    padding: 8px 12px 12px;
  }

  .assignments-card-btn {
    flex: none;
    width: 100%;
    min-height: 44px;
    font-size: 14px;
    white-space: normal;
  }

  .assignments-card-btn-label {
    display: inline;
  }

  .assignments-card-date {
    padding: 0 12px 12px;
    font-size: 13px;
  }

  .assignments-table {
    min-width: 400px;
  }
}

/* ═══════ Hover (pointer devices) ═══════ */
@media (hover: hover) and (pointer: fine) {
  .assignments-add-btn:hover:not(:disabled),
  .assignments-card-btn:hover:not(:disabled),
  .assignments-bulk-btn:hover:not(:disabled) {
    opacity: 0.92;
  }

  .assignments-table-actions .btn:hover {
    background: var(--assign-soft);
  }
}

/* ═══════ Reduced motion ═══════ */
@media (prefers-reduced-motion: reduce) {
  .assignments-add-btn:active:not(:disabled),
  .assignments-card-btn:active:not(:disabled),
  .assignments-bulk-btn:active:not(:disabled) {
    transform: none;
  }
}
</style>
